import type { UserIdentityShape } from '@/components/profile/user-identity';

export type TradingCompetitionPhase = 'upcoming' | 'live' | 'concluded';

/** The social board a competition ranks. Mirrors `LEADERBOARD_KINDS` on the API. */
export type TradingCompetitionBoard = 'traders' | 'backers' | 'creators' | 'boosters';

export type TradingCompetitionRankingSource = 'board' | 'fair_pnl' | 'cash_profit';

/** One sort dimension of a board. The values double as `competitions.metric.*` copy keys. */
export type TradingCompetitionMetric =
  | 'pnl'
  | 'volume'
  | 'trades'
  | 'markets'
  | 'backed'
  | 'backs'
  | 'topMarket'
  | 'totalVolume'
  | 'marketsCreated'
  | 'boostVolume'
  | 'boosts'
  | 'marketsBoosted'
  | 'fairPnl'
  | 'realizedPnl'
  | 'boostsPaid'
  | 'pumpProfit'
  | 'cashProfit'
  | 'cashIn'
  | 'cashOut'
  | 'transfers';

export type TradingCompetitionMetricFormat = 'usdSigned' | 'usd' | 'int';

export interface TradingCompetitionColumn {
  metric: TradingCompetitionMetric;
  format: TradingCompetitionMetricFormat;
}

/** Serializable competition display contract shared by the banner, list rows and the detail hero. */
export interface TradingCompetitionSummary {
  id: string;
  title: string;
  subtitle: string | null;
  board: TradingCompetitionBoard;
  metric: TradingCompetitionMetric;
  phase: TradingCompetitionPhase;
  /** ISO timestamps; countdowns run from the server-supplied `initialNowMs`. */
  start: string;
  end: string;
  bannerImageUrl: string;
  /** D6-encoded USDC, formatted at the edge. */
  prizePoolD6: string;
  rankedParticipants: number;
  rankingSource: TradingCompetitionRankingSource;
}

export interface TradingCompetitionStandingRow {
  /** Fair PnL keeps ineligible wallets visible without assigning them a place. */
  rank: number | null;
  user: UserIdentityShape;
  /** Board metrics keyed by metric; money values are D6 strings, counts are numbers. */
  values: Readonly<Partial<Record<TradingCompetitionMetric, string | number>>>;
  /**
   * D6 this place is in line for, as the API computed it — `null` outside the
   * paid places. Never recomputed here: the tie it splits spans the whole board,
   * which a page of rows cannot see.
   */
  prizeD6: string | null;
}

export type PayoutWalletStatus = 'none' | 'saving' | 'saved' | 'error';

export interface PayoutWalletState {
  status: PayoutWalletStatus;
  address: string | null;
  /** Server rejection reason shown in the error state. */
  message: string | null;
}

export interface CompetitionViewerStanding {
  rank: number | null;
  /** The viewer's value for the competition metric, or null when unranked. */
  value: string | number | null;
  /**
   * D6 the viewer is in line for, as the API computed it — tie-aware, and the
   * frozen prize itself once the results exist. Never recomputed here: a local
   * split would disagree with the payout table the moment anyone ties.
   */
  predictedWinningsD6: string | null;
}

/** Columns per board. The competition metric is hoisted to the first column at render time. */
export const COMPETITION_BOARD_COLUMNS: Readonly<
  Record<TradingCompetitionBoard, readonly TradingCompetitionColumn[]>
> = {
  traders: [
    { metric: 'pnl', format: 'usdSigned' },
    { metric: 'volume', format: 'usd' },
    { metric: 'trades', format: 'int' },
    { metric: 'markets', format: 'int' },
  ],
  backers: [
    { metric: 'backed', format: 'usd' },
    { metric: 'backs', format: 'int' },
    { metric: 'topMarket', format: 'usd' },
  ],
  creators: [
    { metric: 'totalVolume', format: 'usd' },
    { metric: 'marketsCreated', format: 'int' },
    { metric: 'topMarket', format: 'usd' },
  ],
  boosters: [
    { metric: 'boostVolume', format: 'usd' },
    { metric: 'boosts', format: 'int' },
    { metric: 'marketsBoosted', format: 'int' },
  ],
};

export const COMPETITION_ADJUSTED_COLUMNS: Readonly<
  Record<Exclude<TradingCompetitionRankingSource, 'board'>, readonly TradingCompetitionColumn[]>
> = {
  fair_pnl: [
    { metric: 'fairPnl', format: 'usdSigned' },
    { metric: 'realizedPnl', format: 'usdSigned' },
    { metric: 'boostsPaid', format: 'usd' },
    { metric: 'pumpProfit', format: 'usd' },
  ],
  cash_profit: [
    { metric: 'cashProfit', format: 'usdSigned' },
    { metric: 'cashIn', format: 'usd' },
    { metric: 'cashOut', format: 'usd' },
  ],
};

export function competitionRankingMetric(
  competition: Pick<TradingCompetitionSummary, 'metric' | 'rankingSource'>,
): TradingCompetitionMetric {
  switch (competition.rankingSource) {
    case 'fair_pnl':
      return 'fairPnl';
    case 'cash_profit':
      return 'cashProfit';
    case 'board':
      return competition.metric;
  }
}

export function competitionRankingColumns(
  competition: Pick<TradingCompetitionSummary, 'board' | 'metric' | 'rankingSource'>,
): readonly TradingCompetitionColumn[] {
  if (competition.rankingSource !== 'board') {
    return COMPETITION_ADJUSTED_COLUMNS[competition.rankingSource];
  }
  const columns = COMPETITION_BOARD_COLUMNS[competition.board];
  const primary = columns.find((column) => column.metric === competition.metric);
  const rest = columns.filter((column) => column.metric !== competition.metric);
  return primary === undefined ? columns : [primary, ...rest];
}

/** Winners paid from the prize pool. Manual payouts in v1 pay the top ten. */
export const COMPETITION_PRIZE_PLACES = 10;
